import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, CircularProgress,
  Divider, Typography
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createManager } from '../../../services/managerAndStaff/createManager';
import { getStates } from '../../../services/locations/getStates';
import { getCities } from '../../../services/locations/getCities';
import { MdPersonAdd } from 'react-icons/md';

const DESIGNATIONS = [
  'Principal', 'Vice Principal', 'HOD', 'Coordinator',
  'Director', 'Dean', 'Manager', 'Administrator', 'Supervisor', 'Other'
];

const defaultForm = {
  authname: '',
  email: '',
  phone: '',
  organization_name: '',
  designation: '',
  stateid: '',
  cityid: '',
  address: '',
};

const CreateUserDialgue = ({ open, onClose, onSuccess }) => {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  const { data: statesData } = useQuery({
    queryKey: ['states'],
    queryFn: () => getStates(101),
  });
  const states = statesData?.data || [];

  const { data: citiesData } = useQuery({
    queryKey: ['cities', form.stateid],
    queryFn: () => getCities(form.stateid),
    enabled: !!form.stateid,
  });
  const cities = citiesData?.data || [];

  const createManagerMutation = useMutation({
    mutationFn: createManager,
    onSuccess: (data) => {
      if (!data.status) {
        toast.error(data.message);
        return;
      }

      toast.success('Manager created successfully!');
      
      // Reset form and close dialog
      setForm(defaultForm);
      setErrors({});
      onSuccess?.();     // Refresh list
      onClose();         // ✅ Close dialog automatically
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Failed to create manager');
    }
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'stateid') {
      setForm((prev) => ({ ...prev, stateid: value, cityid: '' }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.authname.trim()) newErrors.authname = 'Manager name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!form.organization_name.trim()) newErrors.organization_name = 'Organization name is required';
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    createManagerMutation.mutate({
      authname: form.authname,
      email: form.email,
      phone: form.phone,
      organization_name: form.organization_name,
      designation: form.designation || null,
      stateid: form.stateid || null,
      cityid: form.cityid || null,
      address: form.address || null,
    });
  };

  const handleClose = () => {
    setForm(defaultForm);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MdPersonAdd size={22} />
          Create Manager
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">Account Details</Typography>
          <Divider sx={{ mt: -1 }} />

          <TextField label="Full Name" name="authname" value={form.authname} onChange={handleChange} error={!!errors.authname} helperText={errors.authname} fullWidth />
          <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} fullWidth />
          <TextField label="Phone Number" name="phone" value={form.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone} fullWidth />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Organization Details</Typography>
          <Divider sx={{ mt: -1 }} />

          <TextField label="Organization Name" name="organization_name" value={form.organization_name} onChange={handleChange} error={!!errors.organization_name} helperText={errors.organization_name} fullWidth />

          <TextField select label="Designation (optional)" name="designation" value={form.designation} onChange={handleChange} fullWidth>
            <MenuItem value=""><em>Select Designation</em></MenuItem>
            {DESIGNATIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select label="State (optional)" name="stateid" value={form.stateid} onChange={handleChange} fullWidth>
              <MenuItem value=""><em>Select State</em></MenuItem>
              {states.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>

            <TextField select label="City (optional)" name="cityid" value={form.cityid} onChange={handleChange} fullWidth disabled={!form.stateid}>
              <MenuItem value=""><em>Select City</em></MenuItem>
              {cities.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
          </Box>

          <TextField label="Address (optional)" name="address" value={form.address} onChange={handleChange} multiline rows={2} fullWidth />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={createManagerMutation.isPending}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={createManagerMutation.isPending}>
          {createManagerMutation.isPending ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialgue;